/**
 * Wiki path helper utilities for consistent URL generation
 */
import type { Route } from 'next';

type WikiPathOptions = {
  owner: string;
  repo: string;
  version?: string;
  page?: string;
  isPublicView?: boolean;
};

/**
 * Get the base path prefix for wiki routes
 */
export function getWikiPathPrefix(isPublicView = false): string {
  return isPublicView ? '/wikis' : '/wiki';
}

/**
 * Build a complete wiki path with optional version and page parameters
 */
export function buildWikiPath(options: WikiPathOptions): Route {
  const { owner, repo, version, page, isPublicView = false } = options;

  const pathPrefix = getWikiPathPrefix(isPublicView);

  // Build base path
  let path = `${pathPrefix}/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}`;

  // Add version if provided
  if (version) {
    path += `/${encodeURIComponent(version)}`;
  }

  // Add page query parameter if provided
  if (page) {
    const params = new URLSearchParams();
    params.set('page', page);
    path += `?${params.toString()}`;
  }
  return path as Route;
}

/**
 * Build a wiki search path
 */
export function buildWikiSearchPath(
  isPublicView = false,
  queryParams?: Record<string, string>,
): Route {
  const pathPrefix = getWikiPathPrefix(isPublicView);
  let path = `${pathPrefix}/search`;

  if (queryParams && Object.keys(queryParams).length > 0) {
    const params = new URLSearchParams(queryParams);
    path += `?${params.toString()}`;
  }

  return path as Route;
}

/**
 * Parse a GitHub file URL to extract owner, repo, and file path
 */
export function parseGitHubFileUrl(
  url: string,
): { owner: string; repo: string; filePath: string } | null {
  // Extract path components, excluding query params and hash fragments
  const match = url.match(/github\.com\/([^/]+)\/([^/]+)\/blob\/[^/]+\/([^?#]+)/);
  if (match && match[1] && match[2] && match[3]) {
    return {
      owner: decodeURIComponent(match[1]),
      repo: decodeURIComponent(match[2]),
      filePath: decodeURIComponent(match[3]),
    };
  }
  return null;
}

/**
 * Build a GitHub file URL with proper encoding
 * @param owner - Repository owner
 * @param repo - Repository name
 * @param filePath - Path to the file in the repository
 * @param branch - Branch name (defaults to 'HEAD' which auto-redirects to default branch)
 *
 * Uses 'HEAD' reference by default, which GitHub automatically redirects to the
 * repository's default branch (main, master, or any custom default).
 * This avoids hardcoding assumptions about branch names.
 */
export function buildGitHubFileUrl(
  owner: string,
  repo: string,
  filePath: string,
  branch = 'HEAD',
): string {
  // Encode each component to handle special characters safely
  const encodedOwner = encodeURIComponent(owner);
  const encodedRepo = encodeURIComponent(repo);

  // For branches with slashes (e.g., 'feature/branch-name'), encode each segment separately
  // to preserve slashes in the URL structure
  const encodedBranch = branch
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/');

  // For file paths, we need to encode each segment separately to preserve slashes
  const encodedFilePath = filePath
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/');

  return `https://github.com/${encodedOwner}/${encodedRepo}/blob/${encodedBranch}/${encodedFilePath}`;
}

/**
 * Opens a GitHub file URL in a new tab
 * @param owner - Repository owner
 * @param repo - Repository name
 * @param filePath - Path to the file in the repository
 * @param branch - Branch name (defaults to 'HEAD')
 */
export function openGitHubFile(
  owner: string,
  repo: string,
  filePath: string,
  branch = 'HEAD',
): void {
  const url = buildGitHubFileUrl(owner, repo, filePath, branch);
  window.open(url, '_blank', 'noopener,noreferrer');
}
